function isObject(value: any): boolean {
  const type = typeof value;
  return value != null && (type === 'object' || type === 'function');
}
/**
 * Checks if `value` is an empty object, array.
 *
 * Objects are considered empty if they have no own enumerable string keyed
 * properties.
 *
 * Similarly, maps and sets are considered empty if they have a `size` of `0`.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is empty, else `false`.
 * @example
 *
 * isEmpty(null);
 * // => true
 *
 * isEmpty(true);
 * // => true
 *
 * isEmpty(1);
 * // => true
 *
 * isEmpty([1, 2, 3]);
 * // => false
 *
 * isEmpty({ 'a': 1 });
 * // => false
 */
export function isEmpty<T>(value: T): boolean {
  if (value == null) {
    return true;
  }

  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  if (typeof value === 'string' && value.trim().length === 0) {
    return true;
  }

  if (isObject(value) && !Object.keys(value).length) {
    return true;
  }

  return false;
}

export const omit = <T extends Record<string, any>, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
  return Object.fromEntries(Object.entries(obj).filter(([key]) => !keys.includes(key as K))) as Omit<T, K>;
};

/**
 * Picks specific fields from an object and returns a new object containing only those fields.
 *
 * @template T - The type of the input object.
 * @template K - The keys of the input object to pick.
 * @param obj - The source object from which fields will be picked.
 * @param keys - An array of keys to pick from the source object.
 * @returns A new object containing only the specified keys and their corresponding values.
 * @example
 * const obj = { a: 1, b: 2, c: 3 };
 * pickObjectFields(obj, ["a", "b"]); // { a: 1, b: 2 }
 * pickObjectFields(obj, ["a", "c"]); // { a: 1, c: 3 }
 * pickObjectFields(obj, ["b", "c"]); // { b: 2, c: 3 }
 * pickObjectFields(obj, ["a"]); // { a: 1 }
 */
export const pickObjectFields = <T extends Record<string, any>, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
  return Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key as K))) as Pick<T, K>;
};
